import { FormEvent, FunctionComponent } from 'react'
import { Button, Form, Spinner } from 'react-bootstrap'
import Icon from 'components/ui/icon'

import CheckRow from 'components/ui/checkRow'
import { formatCpf, validateCpf } from '../../../utilities/cpf.util'
import { formatPhoneNumber } from '../../../utilities/phoneNumber.util'

export type DoctorFormValues = {
  name: string
  email: string
  cpf: string
  phoneNumber: string
  notifyUserNow?: boolean
}

type DoctorFormProps = {
  values: DoctorFormValues
  onChange: (field: keyof DoctorFormValues, value: string | boolean) => void
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
  onCancel: () => void
  validated: boolean
  saving: boolean
  submitDisabled?: boolean
  /** Shows the "notify now" option (only when adding a doctor). */
  showNotifyOption?: boolean
}

/** Doctor fields shared by the add and edit pages. */
const DoctorForm: FunctionComponent<DoctorFormProps> = ({
  values,
  onChange,
  onSubmit,
  onCancel,
  validated,
  saving,
  submitDisabled = false,
  showNotifyOption = false,
}) => {
  const handleInputChange = ({
    target,
  }: React.ChangeEvent<HTMLInputElement>) =>
    onChange(target.name as keyof DoctorFormValues, target.value)

  return (
    <Form
      noValidate
      validated={validated}
      onSubmit={onSubmit}
      className="card"
      style={{ maxWidth: '48rem' }}
    >
      <div className="card-body">
        <div className="form-grid form-grid-2">
          <Form.Group className="span-2" controlId="formNome">
            <Form.Label>Nome</Form.Label>
            <Form.Control
              type="text"
              placeholder="Nome"
              name="name"
              value={values.name}
              onChange={handleInputChange}
              required
            />
            <Form.Control.Feedback type="invalid">
              Informe o nome do médico.
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group controlId="formEmail">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              placeholder="nome@email.com"
              name="email"
              value={values.email}
              onChange={handleInputChange}
              required
            />
            <Form.Control.Feedback type="invalid">
              Informe o email do médico.
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group controlId="formPhoneNumber">
            <Form.Label>Telefone</Form.Label>
            <Form.Control
              type="text"
              placeholder="(00) 00000-0000"
              name="phoneNumber"
              value={formatPhoneNumber(values.phoneNumber)}
              onChange={handleInputChange}
              minLength={15}
              maxLength={15}
              required
            />
          </Form.Group>

          <Form.Group controlId="formCPF">
            <Form.Label>CPF</Form.Label>
            <Form.Control
              type="text"
              placeholder="000.000.000-00"
              name="cpf"
              value={formatCpf(values.cpf)}
              onChange={handleInputChange}
              minLength={14}
              maxLength={14}
              required
              isValid={validateCpf(formatCpf(values.cpf))}
            />
            <Form.Control.Feedback type="invalid">
              Informe um CPF válido.
            </Form.Control.Feedback>
          </Form.Group>

          {showNotifyOption && (
            <div className="span-2">
              <CheckRow
                id="formNotifyUserNow"
                checked={!!values.notifyUserNow}
                onChange={(checked) => onChange('notifyUserNow', checked)}
                title="Notificar o médico agora?"
                description="Caso ainda esteja validando os repasses, não envie a notificação sobre a criação da conta agora."
              />
            </div>
          )}
        </div>
      </div>

      <div className="card-foot">
        <Button variant="secondary" type="button" onClick={onCancel}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          type="submit"
          disabled={submitDisabled || saving}
        >
          {saving ? (
            <Spinner animation="border" size="sm" />
          ) : (
            <Icon name="check" size={16} />
          )}
          Salvar
        </Button>
      </div>
    </Form>
  )
}

export default DoctorForm
